// ═══════════════════════════════════════════════════════════════
// V·IANet Flow — FlowDiff (Capa 3: Visual Diff)
// Side-by-side diff visualization between two flowchart versions
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { LoadingSpinner } from '../../../../shared/components/LoadingSpinner';
import { request } from '../../../../shared/services/api';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  useReactFlow,
  type NodeTypes,
  type EdgeTypes,
  type Viewport,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { FLOW_TOKENS as T } from '../shared/flowTokens';
import { NODE_TYPES, EDGE_TYPES } from '../editor/utils/flowSchema';
import type { FlowNode, FlowEdge, FlowNodeData } from '../types/flowchart.types';

// ─── Types ──────────────────────────────────────────────────────────────────

interface DiffEntry {
  nodeId: string;
  status: 'added' | 'removed' | 'modified' | 'unchanged';
}

interface DiffResponse {
  versionA: { nodes: FlowNode[]; edges: FlowEdge[] };
  versionB: { nodes: FlowNode[]; edges: FlowEdge[] };
  changes: DiffEntry[];
}

interface FlowDiffProps {
  flowchartId: string;
  procesoId: string;
  versionA: number;
  versionB: number;
  onClose: () => void;
}

// ─── Diff Colors ────────────────────────────────────────────────────────────

const DIFF_COLORS = {
  added: '#16A34A',
  removed: '#DC2626',
  modified: '#F59E0B',
  unchanged: 'transparent',
} as const;

// ─── Client-side diff computation ───────────────────────────────────────────

function computeLocalDiff(nodesA: FlowNode[], nodesB: FlowNode[]): DiffEntry[] {
  const changes: DiffEntry[] = [];
  const mapA = new Map(nodesA.map((n) => [n.id, n]));
  const mapB = new Map(nodesB.map((n) => [n.id, n]));

  // Nodes in B but not in A -> added
  for (const [id, nodeB] of mapB) {
    if (!mapA.has(id)) {
      changes.push({ nodeId: id, status: 'added' });
    } else {
      const nodeA = mapA.get(id)!;
      const labelA = (nodeA.data as FlowNodeData).label;
      const labelB = (nodeB.data as FlowNodeData).label;
      const typeA = (nodeA.data as FlowNodeData).nodeType;
      const typeB = (nodeB.data as FlowNodeData).nodeType;
      if (labelA !== labelB || typeA !== typeB) {
        changes.push({ nodeId: id, status: 'modified' });
      } else {
        changes.push({ nodeId: id, status: 'unchanged' });
      }
    }
  }

  // Nodes in A but not in B -> removed
  for (const id of mapA.keys()) {
    if (!mapB.has(id)) {
      changes.push({ nodeId: id, status: 'removed' });
    }
  }

  return changes;
}

// ─── Apply diff styling to nodes ────────────────────────────────────────────

function applyDiffStyle(nodes: FlowNode[], changes: DiffEntry[], side: 'A' | 'B'): FlowNode[] {
  const changeMap = new Map(changes.map((c) => [c.nodeId, c.status]));

  return nodes.map((node) => {
    const status = changeMap.get(node.id);
    if (!status || status === 'unchanged') return node;

    // For side A, show removed nodes; for side B, show added nodes
    if (side === 'A' && status === 'added') return node;
    if (side === 'B' && status === 'removed') return node;

    const color = DIFF_COLORS[status];
    return {
      ...node,
      style: {
        ...node.style,
        border: `2px solid ${color}`,
        borderRadius: 4,
      },
    };
  });
}

// ─── Single Diff Canvas ─────────────────────────────────────────────────────

function DiffCanvas({
  nodes,
  edges,
  changes,
  side,
  label,
  syncViewport,
  onViewportChange,
}: {
  nodes: FlowNode[];
  edges: FlowEdge[];
  changes: DiffEntry[];
  side: 'A' | 'B';
  label: string;
  syncViewport: Viewport | null;
  onViewportChange: (vp: Viewport) => void;
}) {
  const { setViewport } = useReactFlow();
  const isUserAction = useRef(true);

  // Sync viewport from the other canvas
  useEffect(() => {
    if (syncViewport && !isUserAction.current) return;
    if (syncViewport) {
      isUserAction.current = false;
      setViewport(syncViewport);
      // Allow user actions after a short delay
      setTimeout(() => {
        isUserAction.current = true;
      }, 100);
    }
  }, [syncViewport, setViewport]);

  const styledNodes = useMemo(() => applyDiffStyle(nodes, changes, side), [nodes, changes, side]);

  const nodeTypes: NodeTypes = useMemo(() => NODE_TYPES as unknown as NodeTypes, []);
  const edgeTypes: EdgeTypes = useMemo(() => EDGE_TYPES as unknown as EdgeTypes, []);

  const handleMove = useCallback(
    (_: unknown, viewport: Viewport) => {
      if (isUserAction.current) {
        onViewportChange(viewport);
      }
    },
    [onViewportChange],
  );

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <div
        style={{
          height: 32,
          backgroundColor: T.color.surfaceSubtle,
          borderBottom: `1px solid ${T.color.border}`,
          display: 'flex',
          alignItems: 'center',
          padding: '0 10px',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontSize: '9px',
            fontWeight: 700,
            fontFamily: T.font.family,
            color: T.color.navy,
          }}
        >
          {label}
        </span>
      </div>

      {/* Canvas */}
      <div style={{ flex: 1, position: 'relative' }}>
        <ReactFlow
          nodes={styledNodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          panOnDrag
          zoomOnScroll
          zoomOnPinch
          fitView
          fitViewOptions={{ padding: 0.2 }}
          onMove={handleMove}
          proOptions={{ hideAttribution: true }}
        >
          <Background
            variant={BackgroundVariant.Dots}
            gap={T.spacing.grid}
            size={0.4}
            color="#D0D5DD"
          />
        </ReactFlow>
      </div>
    </div>
  );
}

// ─── Wrapped Diff Canvas ────────────────────────────────────────────────────

function DiffCanvasWrapped(props: Parameters<typeof DiffCanvas>[0]) {
  return (
    <ReactFlowProvider>
      <DiffCanvas {...props} />
    </ReactFlowProvider>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export default function FlowDiff({
  flowchartId,
  procesoId,
  versionA,
  versionB,
  onClose,
}: FlowDiffProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dataA, setDataA] = useState<{ nodes: FlowNode[]; edges: FlowEdge[] }>({
    nodes: [],
    edges: [],
  });
  const [dataB, setDataB] = useState<{ nodes: FlowNode[]; edges: FlowEdge[] }>({
    nodes: [],
    edges: [],
  });
  const [changes, setChanges] = useState<DiffEntry[]>([]);
  const [viewportA, setViewportA] = useState<Viewport | null>(null);
  const [viewportB, setViewportB] = useState<Viewport | null>(null);

  // ─── Fetch diff data ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!flowchartId) return;
    setLoading(true);
    setError(null);

    const fetchDiff = async () => {
      try {
        // Try the diff endpoint first
        const res = await fetch(
          `/api/v1/flujogramas/edit/${flowchartId}/diff/${versionA}/${versionB}`,
          { headers: { 'Content-Type': 'application/json' } },
        );

        if (res.ok) {
          const body = await res.json();
          const diff: DiffResponse = body.data ?? body;
          setDataA(diff.versionA);
          setDataB(diff.versionB);
          setChanges(diff.changes);
        } else if (res.status === 404) {
          // Fallback: fetch each version individually via request() con Bearer
          const [bodyA, bodyB] = await Promise.all([
            request<Record<string, unknown>>(`/flujogramas/${procesoId}/versions/${versionA}`),
            request<Record<string, unknown>>(`/flujogramas/${procesoId}/versions/${versionB}`),
          ]);
          const flowA = ((bodyA as { data?: unknown }).data ?? bodyA) as {
            contenido?: { nodes?: FlowNode[]; edges?: FlowEdge[] };
          };
          const flowB = ((bodyB as { data?: unknown }).data ?? bodyB) as {
            contenido?: { nodes?: FlowNode[]; edges?: FlowEdge[] };
          };

          const nodesA: FlowNode[] = flowA.contenido?.nodes ?? [];
          const edgesA: FlowEdge[] = flowA.contenido?.edges ?? [];
          const nodesB: FlowNode[] = flowB.contenido?.nodes ?? [];
          const edgesB: FlowEdge[] = flowB.contenido?.edges ?? [];

          setDataA({ nodes: nodesA, edges: edgesA });
          setDataB({ nodes: nodesB, edges: edgesB });
          setChanges(computeLocalDiff(nodesA, nodesB));
        } else {
          throw new Error(`Error ${res.status}`);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al cargar diff');
      } finally {
        setLoading(false);
      }
    };

    void fetchDiff();
  }, [flowchartId, procesoId, versionA, versionB]);

  // ─── Sync handlers ────────────────────────────────────────────────────────
  const handleViewportAChange = useCallback((vp: Viewport) => {
    setViewportB(vp);
  }, []);

  const handleViewportBChange = useCallback((vp: Viewport) => {
    setViewportA(vp);
  }, []);

  // ─── Legend ───────────────────────────────────────────────────────────────
  const legendItems = [
    { label: 'Nuevo', color: DIFF_COLORS.added },
    { label: 'Eliminado', color: DIFF_COLORS.removed },
    { label: 'Modificado', color: DIFF_COLORS.modified },
  ];

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          width: '92vw',
          height: '85vh',
          backgroundColor: T.color.surface,
          borderRadius: 8,
          boxShadow: '0 16px 48px rgba(31,56,100,0.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Top bar */}
        <div
          style={{
            height: 36,
            backgroundColor: T.color.navy,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 16px',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span
              style={{
                fontSize: '10px',
                fontWeight: 700,
                fontFamily: T.font.family,
                color: '#FFFFFF',
              }}
            >
              Comparar versiones — Ed. {String(versionA).padStart(2, '0')} vs Ed.{' '}
              {String(versionB).padStart(2, '0')}
            </span>

            {/* Legend */}
            <div style={{ display: 'flex', gap: 10 }}>
              {legendItems.map((item) => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 2,
                      backgroundColor: item.color,
                    }}
                  />
                  <span
                    style={{
                      fontSize: '7.5px',
                      fontFamily: T.font.family,
                      color: 'rgba(255,255,255,0.8)',
                    }}
                  >
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              width: 24,
              height: 24,
              border: 'none',
              background: 'rgba(255,255,255,0.1)',
              borderRadius: 4,
              color: '#FFFFFF',
              fontSize: 14,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            x
          </button>
        </div>

        {/* Body */}
        {loading && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LoadingSpinner text="Cargando versiones para comparar..." size="sm" />
          </div>
        )}

        {error && (
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span
              style={{ fontSize: '10px', fontFamily: T.font.family, color: T.color.statusRejected }}
            >
              {error}
            </span>
          </div>
        )}

        {!loading && !error && (
          <div style={{ flex: 1, display: 'flex' }}>
            {/* Left canvas — Version A */}
            <DiffCanvasWrapped
              nodes={dataA.nodes}
              edges={dataA.edges}
              changes={changes}
              side="A"
              label={`Version ${versionA}`}
              syncViewport={viewportA}
              onViewportChange={handleViewportAChange}
            />

            {/* Divider */}
            <div style={{ width: 1, backgroundColor: T.color.border, flexShrink: 0 }} />

            {/* Right canvas — Version B */}
            <DiffCanvasWrapped
              nodes={dataB.nodes}
              edges={dataB.edges}
              changes={changes}
              side="B"
              label={`Version ${versionB}`}
              syncViewport={viewportB}
              onViewportChange={handleViewportBChange}
            />
          </div>
        )}
      </div>
    </div>
  );
}
